import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BRAND } from "@/data/site";

// Canonical production origin. Canonical / og:url always point here, never at
// localhost or a preview host, so search engines index a single URL per page.
export const SITE_URL = (process.env.REACT_APP_SITE_URL || process.env.SITE_URL || "https://grabnchat.com").replace(/\/+$/, "");
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og/grabnchat-whatsapp-business-api-platform.png`;
export const absUrl = (path = "/") => (/^https?:\/\//.test(path) ? path : `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`);

function setMeta(name, content, attr = "name") {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let link = document.head.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", rel);
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

/**
 * Per-page SEO tags. Every page passes a unique title + description; the
 * canonical URL defaults to the current path on the production domain.
 *   useSeo({ title, description, path?, ogImage?, ogType?, noindex? })
 */
export function useSeo({ title, description, path, canonical, ogImage, ogType = "website", noindex = false }) {
  const { pathname } = useLocation();
  const url = absUrl(canonical ? canonical.replace(/^https?:\/\/[^/]+/, "") : path || pathname);
  const image = ogImage ? absUrl(ogImage) : DEFAULT_OG_IMAGE;

  useEffect(() => {
    if (title) document.title = title;
    setMeta("description", description);
    setMeta("robots", noindex ? "noindex, follow" : "index, follow, max-image-preview:large");
    setLink("canonical", url);

    setMeta("og:site_name", BRAND.name, "property");
    setMeta("og:locale", "en_IN", "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", url, "property");
    setMeta("og:image", image, "property");
    setMeta("og:image:alt", title, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);
  }, [title, description, url, image, ogType, noindex]);
}

/** Injects one JSON-LD block (keyed by `id`) while the component is mounted. */
export function JsonLd({ id, data }) {
  const json = JSON.stringify(data);
  useEffect(() => {
    if (!data) return undefined;
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = id;
      document.head.appendChild(script);
    }
    script.textContent = json;
    return () => document.getElementById(id)?.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, json]);
  return null;
}

/** FAQPage markup — pass only the questions that are visible on the page. */
export function FaqSchema({ faqs }) {
  if (!faqs || !faqs.length) return null;
  return (
    <JsonLd
      id="faq-schema"
      data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }}
    />
  );
}

/** BreadcrumbList markup. items: [{ name, path }] — Home is prepended. */
export function BreadcrumbSchema({ items }) {
  const all = [{ name: "Home", path: "/" }, ...items];
  return (
    <JsonLd
      id="breadcrumb-schema"
      data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: all.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: absUrl(it.path) })),
      }}
    />
  );
}
